// SPEC-402 M1 — deterministic palette extraction from a reference image (Phase 1, front-end half).
// Downsampled grid sampling → quantize → distance-merge clustering → top N colors + coverage.
// Zero VLM, zero randomness: the same image always yields the same palette, so color values are
// measured instead of eyeballed (design doc §二/§三). Attached images are same-origin /api/img/
// URLs, so canvas reads never taint.

export interface PaletteEntry { hex: string; coverage: number }
export interface PaletteResult { palette: PaletteEntry[]; w: number; h: number }

const MAX_SIDE = 96; // downsample cap — plenty for dominant-color statistics, keeps reads cheap
const MAX_COLORS = 8; // top-N clusters reported to the worker
const MERGE_DIST = 24; // RGB euclidean distance under which quantized buckets merge into one cluster

function toHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('');
}

/**
 * Pure clustering core over raw RGBA bytes (exported for determinism review; no DOM needed).
 * Buckets: 4 bits per channel (16 levels, 4096 buckets) → sort by count → merge near buckets.
 */
export function clusterPixels(data: Uint8ClampedArray, maxColors = MAX_COLORS): PaletteEntry[] {
  const buckets = new Map<number, { count: number; r: number; g: number; b: number }>();
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue; // transparent pixels don't paint the design
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const bk = buckets.get(key);
    if (bk) { bk.count++; bk.r += r; bk.g += g; bk.b += b; }
    else buckets.set(key, { count: 1, r, g, b });
    total++;
  }
  if (total === 0) return [];
  const sorted = [...buckets.values()].sort((a, b) => b.count - a.count);
  const clusters: { count: number; r: number; g: number; b: number }[] = [];
  for (const bk of sorted) {
    const mr = bk.r / bk.count, mg = bk.g / bk.count, mb = bk.b / bk.count;
    let merged = false;
    for (const c of clusters) {
      const cr = c.r / c.count, cg = c.g / c.count, cb = c.b / c.count;
      const dist = Math.sqrt((cr - mr) ** 2 + (cg - mg) ** 2 + (cb - mb) ** 2);
      if (dist < MERGE_DIST) { c.count += bk.count; c.r += bk.r; c.g += bk.g; c.b += bk.b; merged = true; break; }
    }
    if (!merged && clusters.length < maxColors) clusters.push({ ...bk });
    else if (!merged) {
      // over the cluster cap: fold into the nearest cluster so coverage still sums to ~1
      let best = clusters[0], bestDist = Infinity;
      for (const c of clusters) {
        const cr = c.r / c.count, cg = c.g / c.count, cb = c.b / c.count;
        const dist = Math.sqrt((cr - mr) ** 2 + (cg - mg) ** 2 + (cb - mb) ** 2);
        if (dist < bestDist) { best = c; bestDist = dist; }
      }
      if (best) { best.count += bk.count; best.r += bk.r; best.g += bk.g; best.b += bk.b; }
    }
  }
  return clusters
    .sort((a, b) => b.count - a.count)
    .map(c => ({
      hex: toHex(c.r / c.count, c.g / c.count, c.b / c.count),
      coverage: Math.round((c.count / total) * 1000) / 1000,
    }));
}

/**
 * Load a same-origin image URL, downsample, and extract the top-N palette plus pixel dimensions.
 * Returns null on any failure (decode/network/canvas) — callers proceed without a measured
 * palette and the worker's VLM falls back to estimating one (strictly no worse than pre-M1).
 */
export async function extractPaletteFromUrl(url: string, maxColors = MAX_COLORS): Promise<PaletteResult | null> {
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return null;
    const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
    const sw = Math.max(1, Math.round(w * scale));
    const sh = Math.max(1, Math.round(h * scale));
    const cnv = document.createElement('canvas');
    cnv.width = sw;
    cnv.height = sh;
    const ctx = cnv.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, sw, sh);
    const data = ctx.getImageData(0, 0, sw, sh).data;
    return { palette: clusterPixels(data, maxColors), w, h };
  } catch {
    return null;
  }
}
